<template>
  <div v-if="show" class="modal-overlay" @click.self="handleClose">
    <div class="modal-dialog">
      <div class="modal-header">
        <h3 class="modal-title">{{ title }}</h3>
        <button
          type="button"
          class="btn-close"
          @click="handleClose"
        >
          <i class="ri-close-line"></i>
        </button>
      </div>
      <div class="modal-body">
        <div v-if="loading" class="loading-state">
          <i class="ri-loader-4-line spin"></i> 載入可選程式中...
        </div>
        <div v-else-if="availablePrograms.length === 0" class="empty-state">
          <i class="ri-information-line"></i>
          <p>沒有可添加的程式功能</p>
        </div>
        <div v-else>
          <div class="search-box">
            <input
              v-model="searchKeyword"
              type="text"
              class="form-control"
              placeholder="搜尋程式名稱或代碼..."
            />
          </div>
          <div class="available-programs-list">
            <div
              v-for="program in filteredPrograms"
              :key="program.id"
              class="available-program-item-wrapper"
            >
              <div
                class="available-program-item"
                :class="{ selected: selectedProgramIds.includes(program.id) }"
                @click="toggleProgramSelection(program.id)"
              >
                <input
                  type="checkbox"
                  :checked="selectedProgramIds.includes(program.id)"
                  @click.stop="toggleProgramSelection(program.id)"
                />
                <i :class="program.icon || 'ri-file-line'" class="program-icon"></i>
                <div class="program-details">
                  <div class="program-name">{{ program.programName }}</div>
                  <div class="program-meta">
                    <span class="program-code">{{ program.programCode }}</span>
                    <span class="program-path">{{ program.programPath }}</span>
                  </div>
                </div>
              </div>
              
              <!-- 權限選擇區（僅在程式被選中時顯示） -->
              <div
                v-if="selectedProgramIds.includes(program.id) && program.permissions && program.permissions.length > 0"
                class="permissions-selector"
                @click.stop
              >
                <div class="permissions-header">
                  <span class="permissions-label">可用功能：</span>
                  <button
                    type="button"
                    class="btn-link btn-sm"
                    @click="selectAllPermissions(program.id)"
                  >
                    全選
                  </button>
                  <button
                    type="button"
                    class="btn-link btn-sm"
                    @click="clearAllPermissions(program.id)"
                  >
                    允許全部功能
                  </button>
                </div>
                <div class="permissions-checkboxes">
                  <label
                    v-for="perm in program.permissions"
                    :key="perm"
                    class="permission-checkbox-label"
                  >
                    <input
                      type="checkbox"
                      :checked="isProgramPermissionSelected(program.id, perm)"
                      @change="toggleProgramPermission(program.id, perm)"
                      :disabled="perm.endsWith(':read') && isProgramPermissionSelected(program.id, perm) && selectedProgramPermissions[program.id] && selectedProgramPermissions[program.id].length === 1"
                    />
                    <span class="permission-name">{{ formatPermissionName(perm) }}</span>
                  </label>
                </div>
                <div class="permissions-hint">
                  <i class="ri-information-line"></i>
                  read 權限預設勾選且不可取消，點擊「允許全部功能」可允許此程式的所有功能
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-secondary"
          @click="handleClose"
          :disabled="saving"
        >
          取消
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="handleConfirm"
          :disabled="saving || selectedProgramIds.length === 0"
        >
          <span v-if="saving">處理中...</span>
          <span v-else>{{ confirmButtonText }} ({{ selectedProgramIds.length }})</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
  name: 'ProgramSelector',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '添加程式功能'
    },
    confirmButtonText: {
      type: String,
      default: '添加'
    },
    availablePrograms: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    initialSelected: {
      type: Object,
      default: () => ({}) // { programId: [permissions] or null }
    }
  },
  emits: ['close', 'confirm'],
  setup(props, { emit }) {
    const searchKeyword = ref('');
    const selectedProgramIds = ref([]);
    const selectedProgramPermissions = ref({});

    // 當初始選擇改變時，更新內部狀態
    watch(() => props.initialSelected, (newVal) => {
      if (newVal && Object.keys(newVal).length > 0) {
        selectedProgramIds.value = Object.keys(newVal).map(id => id);
        selectedProgramPermissions.value = { ...newVal };
      }
    }, { immediate: true });

    // 當對話框關閉時，重置狀態
    watch(() => props.show, (newVal) => {
      if (!newVal) {
        searchKeyword.value = '';
        selectedProgramIds.value = [];
        selectedProgramPermissions.value = {};
      } else if (props.initialSelected && Object.keys(props.initialSelected).length > 0) {
        // 打開對話框時，恢復初始選擇
        selectedProgramIds.value = Object.keys(props.initialSelected).map(id => id);
        selectedProgramPermissions.value = { ...props.initialSelected };
      }
    });

    const filteredPrograms = computed(() => {
      if (!searchKeyword.value) {
        return props.availablePrograms;
      }
      const keyword = searchKeyword.value.toLowerCase();
      return props.availablePrograms.filter(program =>
        program.programName.toLowerCase().includes(keyword) ||
        program.programCode.toLowerCase().includes(keyword) ||
        (program.programNameEn && program.programNameEn.toLowerCase().includes(keyword))
      );
    });

    const toggleProgramSelection = (programId) => {
      const index = selectedProgramIds.value.indexOf(programId);
      if (index > -1) {
        selectedProgramIds.value.splice(index, 1);
        delete selectedProgramPermissions.value[programId];
      } else {
        selectedProgramIds.value.push(programId);
        // 新選中程式時，預設全選所有權限
        const program = props.availablePrograms.find(p => p.id === programId);
        if (program && program.permissions && program.permissions.length > 0) {
          selectedProgramPermissions.value[programId] = [...program.permissions];
        } else {
          selectedProgramPermissions.value[programId] = null;
        }
      }
    };

    const isProgramPermissionSelected = (programId, permission) => {
      const perms = selectedProgramPermissions.value[programId];
      if (perms === null) {
        return true;
      }
      return perms && perms.includes(permission);
    };

    const toggleProgramPermission = (programId, permission) => {
      if (selectedProgramPermissions.value[programId] === null) {
        const program = props.availablePrograms.find(p => p.id === programId);
        if (program && program.permissions) {
          selectedProgramPermissions.value[programId] = [...program.permissions];
        } else {
          selectedProgramPermissions.value[programId] = [];
        }
      }
      
      if (!selectedProgramPermissions.value[programId]) {
        selectedProgramPermissions.value[programId] = [];
      }
      
      const perms = selectedProgramPermissions.value[programId];
      const index = perms.indexOf(permission);
      
      if (index > -1 && permission.endsWith(':read') && perms.length === 1) {
        return;
      }
      
      if (index > -1) {
        perms.splice(index, 1);
        if (perms.length === 0) {
          const program = props.availablePrograms.find(p => p.id === programId);
          if (program && program.permissions) {
            const readPermission = program.permissions.find(p => p.endsWith(':read'));
            if (readPermission) {
              perms.push(readPermission);
            }
          }
        }
      } else {
        perms.push(permission);
      }
    };

    const selectAllPermissions = (programId) => {
      const program = props.availablePrograms.find(p => p.id === programId);
      if (program && program.permissions) {
        selectedProgramPermissions.value[programId] = [...program.permissions];
      }
    };

    const clearAllPermissions = (programId) => {
      selectedProgramPermissions.value[programId] = null;
    };

    const formatPermissionName = (permission) => {
      const parts = permission.split(':');
      if (parts.length !== 2) return permission;
      
      const actionMap = {
        'read': '讀取',
        'create': '新增',
        'update': '編輯',
        'delete': '刪除',
        'export': '匯出',
        'print': '列印'
      };
      
      const resource = parts[0];
      const action = parts[1];
      
      return `${resource}:${actionMap[action] || action}`;
    };

    const handleClose = () => {
      emit('close');
    };

    const handleConfirm = () => {
      // 準備返回數據格式：{ programIds: [], programPermissions: {} }
      const programPermissions = {};
      selectedProgramIds.value.forEach(programId => {
        const perms = selectedProgramPermissions.value[programId];
        if (perms === null) {
          programPermissions[programId] = null;
        } else if (perms && perms.length > 0) {
          programPermissions[programId] = perms;
        } else {
          const program = props.availablePrograms.find(p => p.id === programId);
          if (program && program.permissions) {
            const readPermission = program.permissions.find(p => p.endsWith(':read'));
            programPermissions[programId] = readPermission ? [readPermission] : null;
          } else {
            programPermissions[programId] = null;
          }
        }
      });

      emit('confirm', {
        programIds: selectedProgramIds.value,
        programPermissions
      });
    };

    return {
      searchKeyword,
      selectedProgramIds,
      selectedProgramPermissions,
      filteredPrograms,
      toggleProgramSelection,
      isProgramPermissionSelected,
      toggleProgramPermission,
      selectAllPermissions,
      clearAllPermissions,
      formatPermissionName,
      handleClose,
      handleConfirm
    };
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.btn-close:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.search-box {
  margin-bottom: 20px;
}

.available-programs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.available-program-item-wrapper {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.available-program-item {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.available-program-item:hover {
  background-color: #f9fafb;
}

.available-program-item.selected {
  background-color: #eff6ff;
  border-color: #3b82f6;
}

.program-icon {
  font-size: 1.5rem;
  color: #6b7280;
}

.program-details {
  flex: 1;
}

.program-name {
  font-weight: 500;
  color: #111827;
  margin-bottom: 4px;
}

.program-meta {
  display: flex;
  gap: 12px;
  font-size: 0.875rem;
  color: #6b7280;
}

.permissions-selector {
  padding: 16px;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.permissions-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.permissions-label {
  font-weight: 500;
  color: #374151;
}

.btn-link {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;
  text-decoration: underline;
}

.btn-link:hover {
  color: #2563eb;
}

.btn-link.btn-sm {
  font-size: 0.8125rem;
}

.permissions-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.permission-checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.875rem;
}

.permission-name {
  color: #374151;
}

.permissions-hint {
  font-size: 0.8125rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>













